import { TabsWithUnderline } from "@truevault/aquilo";
import React from "react";
import { useHistory } from "react-router";
import {
  UseEditPrivacyCenter,
  useEditPrivacyCenter,
} from "../../../components/privacyCenter/EditPrivacyCenter";
import { Routes } from "../../../root/routes";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";

export type PrivacyCenterTabKey =
  | "us-state-notices"
  | "privacy-policy"
  | "international-notices"
  | "theme";

// Note that `enableAllTabs` will override `enableNextTab` if the former is `true` and the latter is `false`
export function PrivacyCenterTabsNew({
  org,
  selectedTabKey,
  trailingContent,
  enableNextTab = true,
  enableAllTabs = true,
}: {
  org: OrganizationDto;
  selectedTabKey: PrivacyCenterTabKey;
  trailingContent?: React.ReactNode;
  enableNextTab?: boolean; // whether the tab immediate to the right of the current tab should be enabled
  enableAllTabs?: boolean; // whether all tabs should be enabled
}) {
  const editPrivacyCenter = useEditPrivacyCenter();
  const history = useHistory();

  const tabsConfig = [
    {
      name: "Privacy Policy",
      tabKey: "privacy-policy",
      route: Routes.privacyCenter.privacyPolicy,
    },
    ...(org.featureGdpr || org.featurePipeda
      ? [
          {
            name: "International Notices",
            tabKey: "international-notices",
            route: Routes.privacyCenter.internationalNotices,
          },
        ]
      : []),
    ...(shouldShowUsStateNoticesTab(editPrivacyCenter)
      ? [
          {
            name: "US State Notices",
            tabKey: "us-state-notices",
            route: Routes.privacyCenter.usStates,
          },
        ]
      : []),
    {
      name: "Theme",
      tabKey: "theme",
      route: Routes.privacyCenter.theme,
    },
  ];

  let selectedTabIdx = tabsConfig.findIndex((tabConfig) => tabConfig.tabKey === selectedTabKey);

  const tabs = (
    <TabsWithUnderline
      maxWidth="max-w-lg"
      tabs={tabsConfig.map((tabConfig, idx) => {
        // `tab` is enabled if (1) it is the current tab, (2) before the current tab, (3) it is the next tab and `enableNextTab` is true, or (4) `enableAllTabs` is true
        let enabled = false;
        if (tabConfig.tabKey === selectedTabKey) {
          enabled = true;
        } else if (idx < selectedTabIdx) {
          enabled = true;
        } else if (idx === selectedTabIdx + 1 && enableNextTab) {
          enabled = true;
        } else if (enableAllTabs) {
          enabled = true;
        }

        return {
          name: tabConfig.name,
          current: tabConfig.tabKey === selectedTabKey,
          onClick: () => {
            if (enabled) {
              history.push(tabConfig.route);
            }
          },
          disabled: !enabled,
        };
      })}
    />
  );

  if (trailingContent) {
    return (
      <div className="tw-boundary">
        <div className="flex items-center justify-between">
          {tabs}
          {trailingContent}
        </div>
      </div>
    );
  } else {
    return <div className="tw-boundary">{tabs}</div>;
  }
}

const shouldShowUsStateNoticesTab = (editPrivacyCenter: UseEditPrivacyCenter) => {
  if (!editPrivacyCenter) return false;

  // The only orgs that don't have the US state notices tab are GDPR-only orgs
  return !editPrivacyCenter.props.privacyCenter?.hideCcpaSection;
};
